<script setup lang="ts">
/** DR-12 · Per-file progress for the existing data-object upload operation. */
import Dialog from '~/components/ui/Dialog.vue'
import Icon from '~/components/ui/Icon.vue'
import ActionButton from '~/components/ui/ActionButton.vue'
import ActionLink from '~/components/ui/ActionLink.vue'
import StatusCell from '~/components/ui/StatusCell.vue'
import ProgressCell from '~/components/ui/ProgressCell.vue'
import FilterChip from '~/components/ui/FilterChip.vue'
import { formatBytes } from '~/utils/dataObjects'

export interface UploadRow {
  key: string
  fileName: string
  sizeBytes: number | null
  phase: string
  progress: number
  problem: string | null
  retryable?: boolean
}
const props = withDefaults(defineProps<{ items: UploadRow[]; title?: string; busy: boolean }>(), { title: 'Add data' })
const emit = defineEmits<{ close: []; retry: [key: string]; cancel: [] }>()

const WORD: Record<string, string> = { queued: 'Queued', creating: 'Creating', uploading: 'Uploading', finalizing: 'Finalizing', checking: 'Reading', ready: 'Ready', refused: 'Refused', retry_waiting: 'Waiting', cancelled: 'Cancelled' }
const word = (phase: string) => WORD[phase] ?? phase
const transferred = (row: UploadRow) => ['finalizing', 'checking', 'ready'].includes(row.phase)
const settled = (row: UploadRow) => ['ready', 'refused', 'cancelled'].includes(row.phase) || (!props.busy && row.phase === 'checking')
const done = computed(() => props.items.filter(transferred).length)
const failed = computed(() => props.items.filter((row) => row.phase === 'refused' || row.phase === 'cancelled').length)
const total = computed(() => props.items.length)
const meta = computed(() => `${done.value} of ${total.value} uploaded${failed.value ? ` · ${failed.value} failed` : ''}`)
const overall = computed(() => total.value ? Math.round(props.items.reduce((sum, row) => sum + (transferred(row) ? 100 : row.phase === 'uploading' ? row.progress : 0), 0) / total.value) : 0)
const view = ref<'all' | 'active' | 'refused'>('all')
const ORDER: Record<string, number> = { refused: 0, uploading: 1, finalizing: 1, checking: 1, creating: 1, retry_waiting: 2, queued: 3, cancelled: 4, ready: 5 }
const rows = computed(() => {
  const picked = view.value === 'active' ? props.items.filter((row) => !settled(row)) : view.value === 'refused' ? props.items.filter((row) => row.phase === 'refused' || row.phase === 'cancelled') : props.items
  return [...picked].sort((left, right) => (ORDER[left.phase] ?? 9) - (ORDER[right.phase] ?? 9))
})
const active = computed(() => props.items.filter((row) => !settled(row)).length)
const percentOf = (row: UploadRow) => transferred(row) ? 100 : row.phase === 'uploading' ? row.progress : ['refused', 'cancelled', 'queued', 'retry_waiting'].includes(row.phase) ? null : row.progress
</script>

<template>
  <Dialog :title="title" :meta="meta" :width="640" @close="!busy && emit('close')">
    <template #icon><Icon name="upload" /></template>
    <div class="flex items-center gap-3">
      <ProgressCell :percent="busy ? overall : done === total ? 100 : overall" :status="busy ? 'Uploading' : failed ? 'Refused' : 'Ready'" :bar-width="120" />
      <span class="flex-1" />
      <template v-if="total > 8">
        <FilterChip label="All" :count="total" :active="view === 'all'" @click="view = 'all'" />
        <FilterChip label="In progress" :count="active" tone="progress" :active="view === 'active'" @click="view = 'active'" />
        <FilterChip label="Refused" :count="failed" tone="refusal" :active="view === 'refused'" @click="view = 'refused'" />
      </template>
    </div>
    <div class="overflow-hidden rounded-md border border-border">
      <div class="grid h-7 items-center gap-x-3 border-b border-border bg-secondary/50 px-3 text-[length:var(--type-meta)] font-semibold text-muted-foreground" style="grid-template-columns: minmax(0,1fr) var(--table-track-96) var(--table-track-128)">
        <span>File</span><span>Status</span><span>Progress</span>
      </div>
      <div class="max-h-[var(--measure-400px)] overflow-auto">
        <div v-for="row in rows" :key="row.key" class="grid min-h-[var(--row-height-detail)] items-center gap-x-3 border-b border-border/50 px-3 last:border-b-0" style="grid-template-columns: minmax(0,1fr) var(--table-track-96) var(--table-track-128)">
          <span class="flex min-w-0 flex-col justify-center py-1.5 leading-[1.25]">
            <span class="flex min-w-0 items-center gap-2 text-xs">
              <Icon name="file" :size="12" class="shrink-0 text-muted-foreground" />
              <span class="truncate">{{ row.fileName }}</span>
            </span>
            <span class="num pl-5 text-[length:var(--type-fine)]" :class="row.problem ? 'whitespace-normal break-words text-destructive' : 'truncate text-muted-foreground'">{{ row.problem ?? (row.sizeBytes === null ? '—' : formatBytes(row.sizeBytes)) }}</span>
          </span>
          <StatusCell :status="word(row.phase)" />
          <span class="flex items-center gap-2">
            <ProgressCell :percent="percentOf(row)" :status="word(row.phase)" :bar-width="48" />
            <ActionLink v-if="row.retryable" label="Retry" variant="muted" @click="emit('retry', row.key)"><template #icon><Icon name="refresh" /></template></ActionLink>
          </span>
        </div>
        <p v-if="!rows.length" class="m-0 px-3 py-4 text-center text-xs text-muted-foreground">{{ view === 'refused' ? 'No file was refused.' : 'Every file has settled.' }}</p>
      </div>
    </div>
    <p v-if="busy" class="num m-0 text-[length:var(--type-fine)] text-muted-foreground">Closing now stops the files that have not been accepted yet · the accepted ones stay.</p>
    <template #actions>
      <ActionButton v-if="busy" label="Stop uploading" variant="danger" @click="emit('cancel')"><template #icon><Icon name="x" /></template></ActionButton>
      <ActionButton v-else label="Close" @click="emit('close')" />
    </template>
  </Dialog>
</template>
